import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { ApiService } from "src/app/shared/services/api.service";
import { EventService } from "src/app/core/services/event.service";
import { TranslateService } from "@ngx-translate/core";
import { of } from "rxjs";
import {  switchMap } from 'rxjs/operators';

@Component({
  selector: "app-change-account-popup",
  templateUrl: "./change-account-popup.component.html",
  styleUrls: ["./change-account-popup.component.scss"],
})
export class ChangeAccountPopupComponent implements OnChanges {
  @Input() dataUser: any[] = [];
  @Input() isLoadingOwners: boolean = false;

  isChangeAccountPopupVisible = false;
  isGuidePopupVisible = false;
  isCheckingQr = false;
  isSubmitting = false;
  selectedShop: any = null;
  selectedNewAccount: any = null;
  needRegenerateQr: boolean = false;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private eventService: EventService,
    private translateService: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["dataUser"] &&
      this.isChangeAccountPopupVisible &&
      this.selectedNewAccount
    ) {
      const updated = this.dataUser.find(
        (a) => a.account_number === this.selectedNewAccount.account_number,
      );
      if (updated) this.selectedNewAccount = updated;
    }
  }

  // Trong component class
  currentStep = 0;

  nextStep(): void {
    if (this.currentStep < 2) {
      this.currentStep++;
    }
  }
  get isSameAccount(): boolean {
    return (
      !!this.selectedShop &&
      !!this.selectedNewAccount &&
      this.selectedShop.terminal_bank_acc ===
        this.selectedNewAccount.account_number
    );
  }
  goToStep(index: number): void {
    this.currentStep = index;
  }

  open(shop: any) {
    this.selectedShop = shop;
    this.selectedNewAccount =
      this.dataUser.find((a) => a.account_number === shop?.terminal_bank_acc) ??
      null;
    this.isChangeAccountPopupVisible = true;
    document.body.style.overflow = "hidden";

    this.isCheckingQr = true;
    this.apiService
      .postItem(`workspaces/${shop?.id}/check-qr-regeneration`, {})
      .subscribe({
        next: (res: any) => {
          this.needRegenerateQr = res?.metadata?.needRegenerateQr || false;
          this.isCheckingQr = false;
        },
        error: (e) => {
          console.error("Lỗi check QR regeneration:", e);
          this.isCheckingQr = false;
        },
      });
  }

  closeChangeAccountPopup() {
    this.isChangeAccountPopupVisible = false;
    this.selectedShop = null;
    this.selectedNewAccount = null;
    document.body.style.overflow = "";
  }

  selectNewAccount(account: any) {
    this.selectedNewAccount = account;
  }

  openGuidePopup() {
    if (this.needRegenerateQr) {
      this.isChangeAccountPopupVisible = false;
      this.isGuidePopupVisible = true;
    } else {
      this.confirmChangeAccount();
    }
  }

  closeGuidePopup() {
    this.isGuidePopupVisible = false;
    this.currentStep = 0;
  }

  confirmChangeAccount() {
    if (!this.selectedNewAccount || this.isSubmitting) return;

    this.isSubmitting = true;
    this.eventService.setLoading(true);

    const account = this.selectedNewAccount;
    const shop = this.selectedShop;

    // Đóng các popup trước
    this.isGuidePopupVisible = false;
    this.closeChangeAccountPopup();

    // TH 1: qr_type === 0 (Cập nhật thông tin Workspace)
    if (shop?.qr_type === 0) {
      const payload = {
        bankName: "970422",
        accountNumber: account.account_number,
        bankAccountName: account.account_owner,
      };

      this.apiService.editItem("workspaces-v2", shop.id, payload).subscribe({
        next: () => {
          this.navigateToSuccess(account, shop);
        },
        error: (err) => this.handleError(err),
      });
      return;
    }

    // TH 2: qr_type !== 0 (Cần lấy OTP / Xác minh QR)
    const otpPayload = {
      newBankAccountNumber: account.account_number,
      newBankAccountName: account.account_owner,
    };

    this.apiService
      .postItem("terminal-qr/get-otp", otpPayload)
      .pipe(
        switchMap((res: any) => {
          // Trường hợp KHÔNG cần xác minh OTP -> Gọi tiếp API identify-qr
          if (!res.metadata?.isVerifyOTP) {
            const identifyData = { ...res?.metadata, otpValue: null, type: 1 };
            return this.apiService
              .postItem("terminal-qr/identify-qr", identifyData)
              .pipe(switchMap(() => of({ isDirectSuccess: true })));
          }
          // Trường hợp CẦN xác minh OTP -> Trả về kết quả để chuyển hướng
          return of({ isDirectSuccess: false, metadata: res.metadata });
        }),
      )
      .subscribe({
        next: (result: any) => {
          if (result.isDirectSuccess) {
            this.navigateToSuccess(account, shop);
          } else {
            this.navigateToOtpPage(account, shop, result.metadata);
          }
        },
        error: (err) => {
          const errorMessage =
            err === "Xác minh lỗi OTP" ? "Mã OTP không chính xác" : err;
          this.handleError(errorMessage);
        },
      });
  }

  // --- Các hàm Helper hỗ trợ điều hướng & xử lý lỗi ---

  private navigateToSuccess(account: any, shop: any) {
    this.isSubmitting = false;
    this.router
      .navigate(["/viettel/acc-change-success"], {
        queryParams: {
          account_number: account.account_number,
          account_owner: account.account_owner,
          account_type: account.account_type,
          workspace_id: shop?.id,
          shop_name: shop?.name,
          need_regenerate_qr: this.needRegenerateQr,
          serial_number: shop?.terminalID,
        },
      })
      .then(() => this.eventService.setLoading(false));
  }

  private navigateToOtpPage(account: any, shop: any, metadata: any) {
    this.isSubmitting = false;
    this.router
      .navigate(["/viettel/change-account-otp"], {
        queryParams: {
          account_number: account.account_number,
          account_owner: account.account_owner,
          account_type: account.account_type,
          workspace_id: shop?.id,
          shop_name: shop?.name,
          need_regenerate_qr: this.needRegenerateQr,
          data_otp: JSON.stringify(metadata),
          serial_number: shop?.terminalID,
        },
      })
      .then(() => this.eventService.setLoading(false));
  }

  private handleError(err: any) {
    this.isSubmitting = false;
    this.eventService.setLoading(false);
    this.apiService.showToastMobile(
      "error",
      this.translateService.instant(err || "Có lỗi xảy ra"),
    );
  }
}
